// Cross-origin auth handoff between workspace shell (my.symbols.app) and
// public preview surfaces (*.preview.symbols.app). The two origins do NOT
// share localStorage, so when a logged-in user lands on a preview URL for
// a PRIVATE project we have to:
//
//   1. Redirect the preview to my.symbols.app/signin?redirect=<previewUrl>
//   2. Workspace signs the user in normally (cookies + localStorage on
//      my.symbols.app)
//   3. Workspace mints a one-shot fragment payload and redirects back:
//      <previewUrl>#__symbols_auth=<base64(json{access,refresh,expiresAt})>
//   4. Preview reads the fragment BEFORE the SDK constructs its
//      TokenManager (which hydrates from localStorage in its constructor),
//      writes the tokens into preview-origin localStorage, then scrubs
//      the fragment from history so the credential never persists in
//      browser history or gets bookmarked.
//
// Fragment (not query) is used because (a) fragments aren't sent to
// the server (no access-log leak) and (b) they aren't part of any
// referer header. The payload itself is short-lived — it carries the
// SDK access token (already in the user's localStorage on
// my.symbols.app), not a freshly minted credential.
//
// Redirect targets are restricted (./handoffTargets.js): same-plane shell
// and preview-app hosts, local hosts from a local page, and — only after the
// server confirms access — hosts that render a user project. A crafted
// `?redirect=` must never hand a session to code the user does not trust.

import { classifyHandoffTarget } from './handoffTargets.js'

// FA204 / frank trap — PREVIEW_HOST_RE/HANDOFF_FRAGMENT_KEY/TOKEN_STORAGE_KEYS/
// NON_PROD_SHELL_HOST_RE/_isNonProdShellHost/_isLocalhostTarget used to live
// at module scope, referenced bare from the exports below (and a dead
// re-export of the first 3 — grep confirmed zero external consumers ever
// imported HANDOFF_FRAGMENT_KEY/TOKEN_STORAGE_KEYS/PREVIEW_HOST_RE by name,
// only the functions that use them). Each export now carries its own
// self-contained copy of whichever consts/helpers it needs.

// Host trust lives in ./handoffTargets.js (`classifyHandoffTarget`) — ONE
// rule, read from the channel registry. Cross-file import, not a same-file
// helper: frank resolves it through globalScope (see canvasUrl.js header).
//
// True only for a target that may receive the session WITHOUT asking the
// server: the same origin, a same-plane shell / preview-app host, or (from
// a local page) a local host. A target that renders a user project returns
// FALSE here — use `confirmHandoffTarget` for those.
export const isTrustedHandoffTarget = (url) => {
  try {
    return classifyHandoffTarget(url).verdict === 'trusted'
  } catch {
    return false
  }
}

// Async gate every minter runs before it hands off. Resolves true when the
// target is trusted outright, or when it renders user projects AND the
// server confirms, for EVERY such project, that it is PRIVATE (a public or
// password-protected project never needs the session — and anyone can read
// a public one, so a read check there proves nothing) AND the signed-in
// user may read it (`getProjectByKey` — the route's own project-access
// gate). Any error, a missing SDK, or a failed probe resolves false.
export const confirmHandoffTarget = async (url, sdk) => {
  let c
  try {
    c = classifyHandoffTarget(url)
  } catch {
    return false
  }
  if (c.verdict === 'trusted') return true
  if (c.verdict !== 'confirm' || !c.projects.length) return false
  if (
    !sdk ||
    typeof sdk.getPublicProjectVisibility !== 'function' ||
    typeof sdk.getProjectByKey !== 'function'
  )
    return false
  try {
    for (const { owner, key } of c.projects) {
      const visibility = await sdk.getPublicProjectVisibility({ owner, key })
      if (visibility !== 'private') return false
      const project = await sdk.getProjectByKey({ owner, key })
      if (!project) return false
    }
    return true
  } catch {
    return false
  }
}

// Build the workspace-side redirect URL: appends `?redirect=<currentUrl>`
// onto `<workspaceOrigin>/signin`. Caller passes the workspace origin
// (resolved via `workspaceOrigin()` from canvasUrl.js).
export const buildLoginRedirectUrl = (workspaceOrigin, returnTo) => {
  const url = new URL('/signin', workspaceOrigin)
  url.searchParams.set('redirect', returnTo)
  return url.toString()
}

// Encode tokens into a fragment payload. Workspace calls this after
// successful login when `?redirect=` points at a trusted preview host.
export const encodeHandoffPayload = ({ accessToken, refreshToken, expiresAt, expiresIn }) => {
  const payload = { a: accessToken, r: refreshToken || '', x: expiresAt || 0, i: expiresIn || 0 }
  // base64url so URL-safe without further escaping.
  const json = JSON.stringify(payload)
  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    return window
      .btoa(unescape(encodeURIComponent(json)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '')
  }
  return Buffer.from(json, 'utf8').toString('base64url')
}

// Append the handoff payload to a URL's fragment. Preserves any existing
// fragment (rare for redirect targets, but defensive).
//  · A RELATIVE target (`?redirect=/x`) resolves against this page — it used
//    to throw (`new URL` with no base) and strand the user on /signin
//    (review D2-N3, 2026-10-09).
//  · A SAME-ORIGIN target shares this page's storage, so it gets NO token: the
//    session is never written into its fragment, where it would land in
//    history (review D2-N2). A stale handoff key in it is dropped.
//  · Only a local cross-origin target (the dev handoff, the one remaining
//    trusted remote shape — handoffTargets.js) still carries the payload.
export const appendHandoffFragment = (url, encodedPayload) => {
  const HANDOFF_FRAGMENT_KEY = '__symbols_auth'
  const here = typeof window !== 'undefined' && window.location ? window.location : null
  const u = here && here.href ? new URL(url, here.href) : new URL(url)
  // Use a key=value fragment shape so the preview can scrub just our key
  // without nuking unrelated SPA state someone might have placed there.
  const existing = u.hash.replace(/^#/, '')
  const parts = existing
    ? existing.split('&').filter((p) => !p.startsWith(`${HANDOFF_FRAGMENT_KEY}=`))
    : []
  const sameOrigin = !!(here && here.origin && u.origin === here.origin)
  if (!sameOrigin) parts.push(`${HANDOFF_FRAGMENT_KEY}=${encodedPayload}`)
  u.hash = parts.join('&')
  return u.toString()
}

// Preview-side: pull the handoff fragment, persist tokens into
// preview-origin localStorage so the SDK's TokenManager picks them up
// during construction, then scrub the fragment from the URL.
//
// MUST be called BEFORE `getSDK()` is first invoked — the TokenManager
// loads from localStorage in its constructor.
export const consumeHandoffFromUrl = () => {
  const HANDOFF_FRAGMENT_KEY = '__symbols_auth'
  const TOKEN_STORAGE_KEYS = {
    accessToken: 'symbols_access_token',
    refreshToken: 'symbols_refresh_token',
    expiresAt: 'symbols_expires_at',
    expiresIn: 'symbols_expires_in'
  }
  const decodeHandoffPayload = (token) => {
    try {
      const padded = token.replace(/-/g, '+').replace(/_/g, '/')
      const json =
        typeof window !== 'undefined'
          ? decodeURIComponent(escape(window.atob(padded)))
          : Buffer.from(padded, 'base64').toString('utf8')
      return JSON.parse(json)
    } catch {
      return null
    }
  }

  if (typeof window === 'undefined') return false
  const hash = window.location.hash || ''
  if (!hash.includes(`${HANDOFF_FRAGMENT_KEY}=`)) return false
  const params = new Map(
    hash
      .replace(/^#/, '')
      .split('&')
      .filter(Boolean)
      .map((p) => {
        const idx = p.indexOf('=')
        return idx === -1 ? [p, ''] : [p.slice(0, idx), p.slice(idx + 1)]
      })
  )
  const token = params.get(HANDOFF_FRAGMENT_KEY)
  if (!token) return false
  const decoded = decodeHandoffPayload(token)
  if (!decoded?.a) return false

  try {
    window.localStorage.setItem(TOKEN_STORAGE_KEYS.accessToken, decoded.a)
    if (decoded.r) {
      window.localStorage.setItem(TOKEN_STORAGE_KEYS.refreshToken, decoded.r)
      // A sign-in on this origin: its refresh token is its own (review D1).
      window.localStorage.setItem('symbols_session_source', 'own')
    }
    if (decoded.x) window.localStorage.setItem(TOKEN_STORAGE_KEYS.expiresAt, String(decoded.x))
    if (decoded.i) window.localStorage.setItem(TOKEN_STORAGE_KEYS.expiresIn, String(decoded.i))
  } catch (err) {
    console.warn('[authHandoff] failed to persist tokens:', err?.message || err)
    return false
  }

  // Scrub the fragment so the credential doesn't sit in browser history
  // or the address bar. Keep any other fragment keys intact.
  params.delete(HANDOFF_FRAGMENT_KEY)
  const remaining = Array.from(params.entries())
    .map(([k, v]) => (v ? `${k}=${v}` : k))
    .join('&')
  const url = new URL(window.location.href)
  url.hash = remaining ? `#${remaining}` : ''
  try {
    window.history.replaceState(null, '', url.toString())
  } catch {
    // history API unavailable; fragment will linger but tokens are persisted.
  }
  return true
}
